import React, { useState } from 'react';
import { FaClock, FaUser, FaFlag, FaEdit, FaTrash, FaCheckSquare, FaSquare } from 'react-icons/fa';
import api from '../../../services/api';

const TaskListView = ({ tasks, statuses, priorities, users, onTaskClick, onTaskEdit, onRefresh }) => {
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleSelectTask = (taskId) => {
    setSelectedTasks(prev => {
      if (prev.includes(taskId)) {
        return prev.filter(id => id !== taskId);
      } else {
        return [...prev, taskId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedTasks.length === tasks.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(tasks.map(t => t.id));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedTasks.length} selected tasks?`)) return;

    try {
      await api.post('/portal/tasks/bulk/delete', { task_ids: selectedTasks });
      setSelectedTasks([]);
      onRefresh();
    } catch (error) {
      console.error('Error deleting tasks:', error);
      alert('Failed to delete tasks');
    }
  };

  const handleBulkStatusUpdate = async (statusId) => {
    try {
      await api.post('/portal/tasks/bulk/update', {
        task_ids: selectedTasks,
        updates: { status_id: statusId }
      });
      setSelectedTasks([]);
      onRefresh();
    } catch (error) {
      console.error('Error updating tasks:', error);
      alert('Failed to update tasks');
    }
  };

  const getStatusName = (statusId) => {
    return statuses.find(s => s.id === statusId)?.name || 'Unknown';
  };

  const getPriorityName = (priorityId) => {
    return priorities.find(p => p.id === priorityId)?.name || 'Unknown';
  };

  const getPriorityColor = (priorityId) => {
    return priorities.find(p => p.id === priorityId)?.color || '#gray';
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return 'Unassigned';
    return user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.email;
  };

  const formatDueDate = (dueDate) => {
    if (!dueDate) return 'No due date';
    const date = new Date(dueDate);
    return date.toLocaleDateString();
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const sortedTasks = [...tasks].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'title':
        comparison = a.title.localeCompare(b.title);
        break;
      case 'status':
        comparison = getStatusName(a.status_id).localeCompare(getStatusName(b.status_id));
        break;
      case 'priority':
        const aPriority = priorities.findIndex(p => p.id === a.priority_id);
        const bPriority = priorities.findIndex(p => p.id === b.priority_id);
        comparison = aPriority - bPriority;
        break;
      case 'due_date':
        const aDate = a.due_date ? new Date(a.due_date) : new Date('9999-12-31');
        const bDate = b.due_date ? new Date(b.due_date) : new Date('9999-12-31');
        comparison = aDate - bDate;
        break;
      case 'created_at':
      default:
        comparison = new Date(a.created_at) - new Date(b.created_at);
        break;
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });

  return (
    <div className="task-list-view">
      {/* Bulk Actions Bar */}
      {selectedTasks.length > 0 && (
        <div className="bulk-actions-bar">
          <span>{selectedTasks.length} task(s) selected</span>
          <div className="bulk-actions">
            <select
              onChange={(e) => handleBulkStatusUpdate(parseInt(e.target.value))}
              className="bulk-action-select"
            >
              <option value="">Change Status...</option>
              {statuses.map(status => (
                <option key={status.id} value={status.id}>{status.name}</option>
              ))}
            </select>
            <button onClick={handleBulkDelete} className="btn-bulk-delete">
              <FaTrash /> Delete
            </button>
            <button onClick={() => setSelectedTasks([])} className="btn-bulk-cancel">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Task List Table */}
      <div className="task-list-table-container">
        <table className="task-list-table">
          <thead>
            <tr>
              <th className="checkbox-col">
                <input
                  type="checkbox"
                  checked={selectedTasks.length === tasks.length && tasks.length > 0}
                  onChange={handleSelectAll}
                />
              </th>
              <th onClick={() => handleSort('title')} className="sortable">
                Task {sortBy === 'title' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('status')} className="sortable">
                Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('priority')} className="sortable">
                Priority {sortBy === 'priority' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th>Assignee</th>
              <th onClick={() => handleSort('due_date')} className="sortable">
                Due Date {sortBy === 'due_date' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedTasks.map(task => (
              <tr
                key={task.id}
                className={selectedTasks.includes(task.id) ? 'selected' : ''}
              >
                <td className="checkbox-col">
                  <input
                    type="checkbox"
                    checked={selectedTasks.includes(task.id)}
                    onChange={() => handleSelectTask(task.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td
                  className="task-title-cell"
                  onClick={() => onTaskClick(task)}
                >
                  <div className="task-title-content">
                    <div
                      className="task-priority-dot"
                      style={{ backgroundColor: getPriorityColor(task.priority_id) }}
                    ></div>
                    <span className="task-title">{task.title}</span>
                  </div>
                </td>
                <td>
                  <span
                    className="status-badge"
                    style={{
                      backgroundColor: statuses.find(s => s.id === task.status_id)?.color + '20',
                      color: statuses.find(s => s.id === task.status_id)?.color
                    }}
                  >
                    {getStatusName(task.status_id)}
                  </span>
                </td>
                <td>
                  <span
                    className="priority-badge"
                    style={{
                      backgroundColor: getPriorityColor(task.priority_id) + '20',
                      color: getPriorityColor(task.priority_id)
                    }}
                  >
                    {getPriorityName(task.priority_id)}
                  </span>
                </td>
                <td>
                  <div className="assignee-cell">
                    <FaUser className="assignee-icon" />
                    {getUserName(task.assigned_to)}
                  </div>
                </td>
                <td>
                  <div className={`due-date-cell ${isOverdue(task.due_date) ? 'overdue' : ''}`}>
                    {task.due_date && <FaClock className="due-date-icon" />}
                    {formatDueDate(task.due_date)}
                  </div>
                </td>
                <td className="actions-cell">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onTaskEdit(task);
                    }}
                    className="btn-icon btn-edit"
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {tasks.length === 0 && (
          <div className="empty-state">
            <p>No tasks found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskListView;
